/**
 * The one place a vendor's display name is written.
 *
 * These names were literals in 29 files across six features, behind six
 * separate label maps that each covered a different subset — which is how the
 * product ended up spelling one vendor two ways. Fixing the spellings without
 * fixing that would just reset the clock: SD-12588 corrected "HouseCall Pro"
 * and "ServiceFusion" inside the Integrations feature and still took four
 * review findings, each another literal the sweep had missed.
 *
 * Deliberately a leaf module with no imports, so features that already depend
 * on each other can read from it without an import cycle.
 *
 * Keys are the integration `type` the backend uses, so a call site that has an
 * id never has to translate. Names follow the vendor's own casing — Housecall
 * Pro and Service Fusion write themselves that way, whatever the catalog API
 * returns.
 */
export const INTEGRATION_NAMES = {
  // FSM
  acculynx: "AccuLynx",
  aptive: "Aptive",
  buildops: "BuildOps",
  customcrm: "Custom FSM",
  fieldroutes: "FieldRoutes",
  housecallpro: "Housecall Pro",
  jobnimbus: "JobNimbus",
  maidcentral: "MaidCentral",
  realgreen: "RealGreen",
  servicefusion: "Service Fusion",
  servicetitan: "ServiceTitan",
  // Calendar
  google_calendar: "Google Calendar",
  outlook_calendar: "Outlook Calendar",
  // Lead platforms
  angi: "Angi",
  google_lsa: "Google Local Services",
  leadperfection: "LeadPerfection",
  thumbtack: "Thumbtack",
  // "Yelp leads" in the catalog: the connector handles leads, not the listing.
  yelp: "Yelp leads",
  // CRM
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  watsco_oca: "Watsco OCA",
  // Telephony
  eight_by_eight: "8x8",
  voice_for_pest: "Voice for Pest",
  voice_for_pest_sip: "Voice for Pest — SIP trunk (AI Receptionist)",
  // Sameday's own books, not a company integration.
  quickbooks: "QuickBooks",
  // Connected-app catalog products, keyed by the catalog slug the directory
  // routes on (`apps?toolkit=<slug>`). The catalogs ship these with provider
  // casing ("Metaads", "Google search console"); the product's own name wins.
  metaads: "Meta Ads",
  google_search_console: "Google Search Console",
  linkedin_ads: "LinkedIn Ads",
  active_campaign: "ActiveCampaign",
  googleforms: "Google Forms",
  googlephotos: "Google Photos",
  googlecontacts: "Google Contacts",
  google_cloud_vision: "Google Cloud Vision",
  google_address_validation: "Google Address Validation",
  microsoft_teams: "Microsoft Teams",
  microsoft_power_bi: "Microsoft Power BI",
  microsoft_clarity: "Microsoft Clarity",
  one_drive: "OneDrive",
  share_point: "SharePoint",
  mx_technologies: "MX Technologies",
  zoho_books: "Zoho Books",
  zoho_inventory: "Zoho Inventory",
  zoho_desk: "Zoho Desk",
  zoho_mail: "Zoho Mail",
  zoho_invoice: "Zoho Invoice",
  html_to_image: "HTML to Image",
  big_data_cloud: "BigDataCloud",
  breezy_hr: "Breezy HR",
  survey_monkey: "SurveyMonkey",
  seat_geek: "SeatGeek",
  highlevel: "HighLevel",
  zoominfo: "ZoomInfo",
  calendarhero: "CalendarHero",
  freshbooks: "FreshBooks",
  paypal: "PayPal",
  docusign: "DocuSign",
  cloudconvert: "CloudConvert",
  clickup: "ClickUp",
  bamboohr: "BambooHR",
  neverbounce: "NeverBounce",
  postgrid: "PostGrid",
  tinyurl: "TinyURL",
  gatherup: "GatherUp",
  junglescout: "Jungle Scout",
  addressfinder: "AddressFinder",
  posthog: "PostHog",
  repairshopr: "RepairShopr",
  servicem8: "ServiceM8",
  maintainx: "MaintainX",
  optimoroute: "OptimoRoute",
  route4me: "Route4Me",
  taxjar: "TaxJar",
  netsuite: "NetSuite",
  rocket_reach: "RocketReach",
  getprospect: "GetProspect",
  fullenrich: "FullEnrich",
  bettercontact: "BetterContact",
  crustdata: "CrustData",
  news_api: "News API",
  tripadvisor_content_api: "Tripadvisor Content API",
  contentful_graphql: "Contentful GraphQL",
  here: "HERE",
} as const;

type IntegrationNameKey = keyof typeof INTEGRATION_NAMES;

/**
 * Display name for an integration type. Falls back to whatever the caller has
 * — usually the catalog API's own spelling — so an integration we haven't
 * named yet still renders something rather than a raw id.
 */
export const integrationName = (type: string, fallback = ""): string =>
  INTEGRATION_NAMES[type as IntegrationNameKey] ?? fallback;

// The display-name helper lives beside the registry it reads: the Operator chat
// and the Integrations pages both use it, and a module of its own would become a
// shared chunk and one more request on the chat route.

/**
 * Customer-facing names for catalog apps. A product the vendor registry
 * names (`INTEGRATION_NAMES`, keyed by the catalog slug) reads as the
 * registry spells it; any other name is cleaned of the provider's
 * whitespace and implementation suffixes ("Klaviyo MCP"), which mean nothing
 * to a customer. Identity matching and deduplication still use the provider's
 * raw slug and name; this only changes what the card shows.
 */
const IMPLEMENTATION_SUFFIX = /\s+mcp$/i;

/** "google_search_console" reads as "Google Search Console". */
const humanizeSlug = (slug: string): string =>
  slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * The name a card, page or dialog shows for a catalog app. `name` is the
 * provider's spelling, used when the registry has none; without it the slug
 * reads as words.
 */
export function catalogDisplayName(
  slug: string,
  name: string = humanizeSlug(slug),
): string {
  const registered = integrationName(slug);
  if (registered) return registered;
  const trimmed = name.replace(/\s+/g, " ").trim();
  return trimmed.replace(IMPLEMENTATION_SUFFIX, "") || trimmed;
}
